import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { db } from "../firebaseConfig";
import { deleteDoc, doc, onSnapshot, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import locations from "../spyfallLocations";

export default function SpyfallGame() {
  const { roomId } = useParams();
  const [data, setData] = useState(null);
  const [myName, setMyName] = useState("");
  const [showRole, setShowRole] = useState(false);
  const navigate = useNavigate();
  const isCreator = localStorage.getItem("isCreator") === "true";

  const deleteRoom = async (roomId) => {
    try {
      await deleteDoc(doc(db, "rooms", roomId)); // Delete the room document
      console.log("Room deleted successfully");
    } catch (error) {
      console.error("Error deleting room: ", error);
    }
  };

  useEffect(() => {
    const name = localStorage.getItem("spyfallName");
    setMyName(name || "");
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "rooms", roomId), (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setData(d);
        if (d.started === false) {
          navigate("/spyfall");
        }
      }
    });
    return () => unsub();
  }, [roomId, navigate]);

  if (!data || !myName) return <p>Loading game...</p>;

  const role = data.roles?.[myName];
  const isSpy = role === "spy";
  const realLocation = data.location;
  const allLocs = data.locationList || data.locations || locations;

  const chunkLocations = (arr, size) => {
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  };

  const handleEndGame = async () => {
    await updateDoc(doc(db, "rooms", roomId), {
      started: false,
      location: null,
      roles: null,
      showFullLocation: false
    });
    await deleteRoom(roomId);
    navigate("/spyfall");
  };

  const handlePlayAgain = async () => {
    const list = locations.sort(() => 0.5 - Math.random()).slice(0, 30).sort((a, b) => a.localeCompare(b));
    const loc = list[Math.floor(Math.random() * list.length)];
    const spyIdx = Math.floor(Math.random() * data.players.length);
    const newRoles = {};
    data.players.forEach((p, i) => {
      newRoles[p.name] = i === spyIdx ? "spy" : "agent";
    });
    await updateDoc(doc(db, "rooms", roomId), {
      started: true,
      location: loc,
      roles: newRoles,
      locationList: list,
      showFullLocation: false
    });
    setShowRole(false);
  };

  const handleRevealLocation = async () => {
    await updateDoc(doc(db, "rooms", roomId), {
      showFullLocation: true
    });
  };

  return (
    <div style={styles.container}>
      <h1>Spyfall</h1>
      <h2>Welcome, {myName}</h2>

      <button style={styles.toggleRoleButton} onClick={() => setShowRole(!showRole)}>
        {showRole ? "Hide Role" : "Reveal Role"}
      </button>
      {showRole && (
        <p style={styles.roleText}>
          Your role: <strong>{isSpy ? "Spy" : "Agent"}</strong>
        </p>
      )}
      {!isSpy && showRole && (
        <p style={styles.locationText}>
          🕵️‍♀️ Your location: <strong>{realLocation}</strong>
        </p>
      )}
      {isSpy && showRole && (
        <p style={styles.spyText}>🤫 You don’t know the location!</p>
      )}

      <div style={styles.locationsContainer}>
        <h3>All Locations</h3>
        <table style={styles.table}>
          <tbody>
            {chunkLocations(allLocs, 2).map((row, i) => (
              <tr key={i}>
                {row.map((loc, idx) => (
                  <td key={idx} style={styles.td}>{loc}</td>
                ))}
                {row.length === 1 && <td style={styles.td}></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Creator reveal location at end of game */}
      {isCreator && !data.showFullLocation && (
        <button style={styles.revealBtn} onClick={handleRevealLocation}>
          Reveal Location to All
        </button>
      )}

      {data.showFullLocation && (
        <p style={styles.finalLocationText}>
          🏁 The location was: <strong>{realLocation}</strong>
        </p>
      )}

      {isCreator && (
        <div style={styles.controls}>
          <button style={styles.playAgainButton} onClick={handlePlayAgain}>
            🔁 Play Again
          </button>
          <button style={styles.endButton} onClick={handleEndGame}>
            ❌ End Game
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: "700px",
    margin: "2rem auto",
    padding: "2rem",
    backgroundColor: "#fdfdfd",
    borderRadius: "16px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    fontFamily: "'Poppins', sans-serif",
    textAlign: "center",
    color: "#333",
  },
  toggleRoleButton: {
    padding: "0.5rem 1rem",
    fontSize: "1rem",
    borderRadius: "6px",
    border: "none",
    backgroundColor: "#4e6cff",
    color: "#fff",
    cursor: "pointer",
    marginBottom: "1rem",
  },
  roleText: {
    fontSize: "1.2rem",
    margin: "0.5rem 0",
  },
  locationText: {
    margin: "0.5rem 0",
  },
  spyText: {
    margin: "0.5rem 0",
    fontStyle: "italic",
  },
  locationsContainer: {
    marginTop: "2rem",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "1rem",
  },
  td: {
    padding: "0.5rem",
    textAlign: "center",
    borderBottom: "1px solid #ddd",
    width: "50%",
  },
  revealBtn: {
    marginTop: "1rem",
    padding: "0.6rem 1.2rem",
    fontSize: "1rem",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "#ffa726",
    color: "#fff",
    cursor: "pointer",
  },
  finalLocationText: {
    marginTop: "1rem",
    fontSize: "1.1rem",
    fontWeight: "bold",
  },
  controls: {
    marginTop: "2rem",
  },
  endButton: {
    marginLeft: "1rem",
    padding: "0.6rem 1.2rem",
    fontSize: "1rem",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "#ff4e4e",
    color: "#fff",
    cursor: "pointer",
  },
  playAgainButton: {
    padding: "0.6rem 1.2rem",
    fontSize: "1rem",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "#4caf50",
    color: "#fff",
    cursor: "pointer",
  },
};
